<script setup lang="ts" generic="K">
import { type VisitWithFields } from "@/api/visits";
import MethodIcon from "@/components/common/MethodIcon/MethodIcon.vue";
import EmptyState from "@/components/common/EmptyState/EmptyState.vue";
import CoreLink from "@/components/common/CoreLink/CoreLink.vue";
import CoreButton from "@/components/common/CoreButton/CoreButton.vue";
import StatusSelectorVisitor from "@/components/common/StatusSelector/StatusSelectorVisitor.vue";
import { ElTableColumn } from "element-plus";
import TimelineActionButton from "@/components/common/TimelineActionButton/TimelineActionButton.vue";
import TableSearch from "@/components/inputs/TableSearch.vue";
import DateRangePicker from "@/components/common/DateRangePicker/DateRangePicker.vue";
import DateShortcutSelector from "@/components/common/DateShortcutSelector/DateShortcutSelector.vue";
import DataTable, {
  type SortChangedEvent,
} from "@/components/common/DataTable/DataTable.vue";
import CoreAvatar from "@/components/common/CoreAvatar/CoreAvatar.vue";
import ColumnSelector, {
  type ColumnSelectionGroup,
} from "@/components/common/ColumnSelector/ColumnSelector.vue";
import { useScreenType } from "@/composables/useScreenType";
import { formatFullName } from "@/utils/string";
import { useDateUtils } from "@/composables/dates";
import { useLocalStorage } from "@vueuse/core";
import { useI18n } from "vue-i18n";
import { ref, watch, computed } from "vue";
import { useGetVisitorFields } from "@/api/visitorFields";
import { useLocationSelector } from "@/composables/location";
import { type TableAction } from "../types";
import { useVisitField } from "@/composables/visitField";
import useVisitStore from "@/stores/visit";
import type { Status } from "@/api/employeeMovements";
import { useDownloadUtils } from "@/composables/download";
import useRouteQueryParams from "@/composables/routeQueryParams";
import { SortDirection } from "@/api/enums/sorting";
import { type DateRangeFilter } from "@/composables/dateShortcuts";
import DateWithTimeZone from "@/components/common/DateWithTimeZone/DateWithTimeZone.vue";
import { useVisits } from "@/composables/visits";
import { useSubscription } from "@/composables/subscription";
import { useGetDevices } from "@/api/devices";
import { useSvgQrCode } from "@/composables/svgQrCode";

export interface Props {
  tableData?: VisitWithFields[];
  actions: TableAction[];
  isLoading: boolean;
  currentPage: number;
  numberOfPages: number;
  defaultSortField: string;
  defaultSortOrder: "asc" | "desc";
  searchFilter: string;
  dateRangeFilter: DateRangeFilter;
  hasFilters: boolean;
}

const props = defineProps<Props>();

const emit = defineEmits<{
  (e: "page-change", page: number): void;
  (e: "sort-change", sort: string): void;
  (e: "selection-change", rows: VisitWithFields[]): void;
  (e: "row-click", visit: VisitWithFields): void;
  (e: "action-selected", action: string): void;
  (e: "add-visit"): void;
  (e: "visitor-status-changed", status: Status, visitId: string): void;
  (e: "update:search-filter", value: string): void;
  (e: "update:date-range-filter", value: DateRangeFilter): void;
}>();

const { t } = useI18n();

const VISITORS_TIMELINE_SELECTION_KEY = "column-selector-visitors-timeline";

const tableRef = ref();
const pageNumber = ref(props.currentPage);
const qrCodeImage = ref("");

const { duration } = useVisits();
const { setQueryParam, queryParamToSort, isDateRangeChanged } =
  useRouteQueryParams();
const { formatDateTime } = useDateUtils();
const { isMobile } = useScreenType();
const { selectedLocationGroupPk, selectedLocation } = useLocationSelector();
const { visitFieldValue } = useVisitField();
const { visitLoadings } = useVisitStore();
const { downloadDocument, isDownloading } = useDownloadUtils();
const { isFreeTrial } = useSubscription();
const { generateQrCode } = useSvgQrCode();
const { data: visitorFields } = useGetVisitorFields({
  locationGroupId: selectedLocationGroupPk.value,
});
const { data: devicesData } = useGetDevices({
  options: { ignoreLocationGroup: true },
});

const defaultSort = computed(() =>
  queryParamToSort(props.defaultSortField, props.defaultSortOrder)
);

const columns = ref<ColumnSelectionGroup[]>([
  {
    items: [
      {
        labelKey: "visitors.timeline.columns.visitor",
        key: "fullName",
        value: true,
        disabled: true,
      },
      {
        labelKey: "visitors.timeline.columns.photo",
        key: "photo",
        value: true,
      },
      {
        labelKey: "visitors.timeline.columns.host",
        key: "host",
        value: true,
      },
      {
        labelKey: "visitors.timeline.columns.category",
        key: "category",
        value: true,
      },
      {
        labelKey: "visitors.timeline.columns.location",
        key: "location",
        value: true,
      },
      {
        labelKey: "visitors.timeline.columns.signedIn",
        key: "startedAt",
        value: true,
      },
      {
        labelKey: "visitors.timeline.columns.signedOut",
        key: "finishedAt",
        value: true,
      },
      {
        labelKey: "visitors.timeline.columns.duration",
        key: "duration",
        value: true,
      },
    ],
  },
  {
    labelKey: "visitors.timeline.columns.customFields",
    items: [],
  },
]);

const setCurrentRow = (row?: object) => {
  tableRef.value?.setCurrentRow(row);
};

defineExpose({
  columns,
  setCurrentRow,
});

const formatHost = (row: VisitWithFields) => {
  const user = row.host;
  const isCompany = user?.firstName === "Company";
  if (!user || isCompany) return t("emptyValue");
  return formatFullName(user.firstName, user.lastName);
};

const localStorage = useLocalStorage<{ items: ColumnSelectionGroup[] }>(
  VISITORS_TIMELINE_SELECTION_KEY,
  { items: [...columns.value] }
);

const visibleFields = computed(
  () => visitorFields.value?.filter(({ id }) => columnVisible(`cf-${id}`))
);

const isNotMatchingData = computed(
  () =>
    (!props.tableData?.length && props.searchFilter) ||
    props.hasFilters ||
    isDateRangeChanged(props.dateRangeFilter)
);

const columnVisible = (key: string) =>
  columns.value.some(
    (group: ColumnSelectionGroup) =>
      !!group.items.find((item) => item.key === key)?.value
  );

watch(
  visitorFields,
  (newFields) => {
    if (!newFields) return;

    columns.value[1].items = newFields.map(({ id, title }) => {
      const key = `cf-${id}`;
      const localStorageField = localStorage.value.items[1].items.find(
        (field) => field.key === key
      );
      return {
        key,
        labelKey: title,
        value: localStorageField?.value ?? false,
      };
    });
    localStorage.value.items[1].items = columns.value[1].items;
  },
  { immediate: true }
);

const locationDeviceId = computed(
  () =>
    devicesData.value?.list.find(
      (device) => device.location.pk === selectedLocation.value.id
    )?.pin
);

watch(
  () => selectedLocation.value,
  async (newLocation) => {
    if (!newLocation.id) return;
    const { data } = await generateQrCode("png", newLocation.id);
    if (!data.qrCode) return;
    qrCodeImage.value = data.qrCode;
  },
  { immediate: true }
);

const handleDownloadDocument = async (
  fieldPk: string,
  visit?: VisitWithFields
) => {
  const { url } = visitFieldValue(fieldPk, visit) ?? {};
  if (!url) return;
  const key = `${fieldPk}_${visit?.id}`;

  await downloadDocument(url, url, key, visit?.fullName);
};

const handleSortChanged = async ({ order, column }: SortChangedEvent) => {
  let sort = `${props.defaultSortField}:${props.defaultSortOrder}`;

  if (order && column.property) {
    sort = `${column.property}:${SortDirection[order]}`;
  }

  await setQueryParam(
    "sort",
    sort,
    `${props.defaultSortField}:${props.defaultSortOrder}`
  );
  emit("sort-change", sort);
};

const handleRowClicked = (row: VisitWithFields) => {
  if (row.anonymizedAt) return;
  emit("row-click", row);
};
</script>

<template>
  <DataTable
    ref="tableRef"
    v-model:page-number="pageNumber"
    :data="tableData"
    :number-of-pages="numberOfPages"
    :is-loading="isLoading"
    :default-sort="defaultSort"
    @row-click="handleRowClicked($event)"
    @update:page-number="
      (pageNumber: number) => emit('page-change', pageNumber)
    "
    @update:selected-objects="emit('selection-change', $event)"
    @sort-change="handleSortChanged"
  >
    <template #actions-left>
      <div class="filters-container">
        <TableSearch
          :model-value="searchFilter"
          :placeholder="t('visitors.timeline.actions.searchPlaceholder')"
          @update:model-value="emit('update:search-filter', $event)"
        />
        <component
          :is="isMobile ? DateShortcutSelector : DateRangePicker"
          :date-range-filter="dateRangeFilter"
          @update:model-value="emit('update:date-range-filter', $event)"
        />
      </div>
      <slot />
    </template>
    <template #actions-right>
      <TimelineActionButton
        :actions="actions"
        class="action-dropdown"
        @select="(action: string) => emit('action-selected', action)"
      />
      <CoreButton
        type="primary"
        class="add-visit-btn"
        @click="emit('add-visit')"
      >
        {{ t("visitors.timeline.actions.newVisitButton") }}
      </CoreButton>
    </template>
    <template #columns>
      <el-table-column
        type="selection"
        width="45"
        :fixed="isMobile"
      />
      <el-table-column
        prop="status"
        :label="t('visitors.timeline.columns.status')"
        width="120"
        align="center"
        :fixed="isMobile"
      >
        <template #default="{ row }: { row: VisitWithFields }">
          <div class="status">
            <CoreAvatar
              v-if="columnVisible('photo')"
              :src="row.photoIn ?? ''"
              :icon="row.anonymizedAt ? ['fal', 'eye-slash'] : undefined"
              :is-anonymized="Boolean(row.anonymizedAt)"
            />
            <StatusSelectorVisitor
              v-if="!row.anonymizedAt"
              :visit="row"
              :loading="visitLoadings[row.id]"
              size="small"
              :class="[{ 'status-selector': columnVisible('photo') }]"
              @change="
                (status: Status) =>
                  emit('visitor-status-changed', status, row.id)
              "
            />
          </div>
        </template>
      </el-table-column>
      <el-table-column
        v-if="columnVisible('fullName')"
        sortable="custom"
        prop="fullName"
        :label="t('visitors.timeline.columns.visitor')"
        min-width="180"
      >
        <template #default="{ row }: { row: VisitWithFields }">
          <strong>{{ row.fullName }}</strong>
          <br />
          {{ row.companyName || t("emptyValue") }}
        </template>
      </el-table-column>
      <el-table-column
        v-if="columnVisible('host')"
        prop="host"
        :label="t('visitors.timeline.columns.host')"
        min-width="180"
        :formatter="formatHost"
      >
      </el-table-column>
      <el-table-column
        v-if="columnVisible('category')"
        prop="category"
        :label="t('visitors.timeline.columns.category')"
        :formatter="
          (row: VisitWithFields) => row.categoryName ?? t('emptyValue')
        "
        min-width="180"
      >
      </el-table-column>
      <el-table-column
        v-if="columnVisible('location')"
        prop="location_name"
        :label="t('visitors.timeline.columns.location')"
        :formatter="
          (row: VisitWithFields) => row.location?.name ?? t('emptyValue')
        "
        min-width="180"
      />
      <el-table-column
        v-if="columnVisible('startedAt')"
        sortable="custom"
        prop="startedAt"
        :label="t('visitors.timeline.columns.signedIn')"
        width="200"
      >
        <template #default="{ row }: { row: VisitWithFields }">
          <div class="method">
            <MethodIcon :source="row.sourceIn" />
            <DateWithTimeZone
              v-if="row.startedAt"
              :range="formatDateTime(row.startedAt)"
              :time-zone="row.location?.timeZone"
            />
            <span v-else>{{ t("emptyValue") }}</span>
          </div>
        </template>
      </el-table-column>
      <el-table-column
        v-if="columnVisible('finishedAt')"
        prop="finishedAt"
        sortable="custom"
        :label="t('visitors.timeline.columns.signedOut')"
        width="200"
      >
        <template #default="{ row }: { row: VisitWithFields }">
          <div class="method">
            <MethodIcon
              v-if="row.sourceOut"
              :source="row.sourceOut"
            />
            <DateWithTimeZone
              v-if="row.finishedAt"
              :range="formatDateTime(row.finishedAt)"
              :time-zone="row.location?.timeZone"
            />
            <span v-else>{{ t("emptyValue") }}</span>
          </div>
        </template>
      </el-table-column>
      <el-table-column
        v-if="columnVisible('duration')"
        :label="t('visitors.timeline.columns.duration')"
        min-width="150px"
      >
        <template #default="{ row }: { row: VisitWithFields }">
          {{ duration(row) }}
        </template>
      </el-table-column>
      <el-table-column
        v-for="field in visibleFields"
        :key="field.id"
        :label="field.title"
        prop="visitorField"
        min-width="250px"
        class-name="custom-field-column"
      >
        <template #default="{ row }: { row: VisitWithFields }">
          <ul v-if="visitFieldValue(field.id, row).isArray">
            <li
              v-for="(value, index) in visitFieldValue(field.id, row).value"
              :key="index"
            >
              {{ value }}
            </li>
          </ul>
          <span v-else-if="visitFieldValue(field.id, row).isDocument">
            <CoreButton
              :loading="isDownloading[`${field.id}_${row.id}`]"
              text
              type="primary"
              :icon="['fal', 'file-image']"
              @click.stop="() => handleDownloadDocument(field.id, row)"
            >
              {{ t("visitors.fields.downloadDocument") }}
            </CoreButton>
          </span>
          <span
            v-else
            class="break-word"
          >
            {{ visitFieldValue(field.id, row).value }}
          </span>
        </template>
      </el-table-column>
      <ColumnSelector
        v-model="columns"
        :selection-key="VISITORS_TIMELINE_SELECTION_KEY"
      />
    </template>
    <template #empty-state>
      <EmptyState
        v-if="isNotMatchingData"
        :icon="['fal', 'magnifying-glass']"
        :heading="t('visitors.timeline.emptyState.noMatchingResults')"
      />

      <EmptyState
        v-else-if="isFreeTrial"
        :icon="['fal', 'user-plus']"
        :heading="t('visitors.timeline.emptyState.trial.heading')"
      >
        <div class="subheading-trial">
          <strong>{{
            t("visitors.timeline.emptyState.trial.subheading")
          }}</strong>
        </div>

        <span class="description-trial">{{
          t("visitors.timeline.emptyState.trial.description")
        }}</span>

        <i18n-t
          keypath="visitors.timeline.emptyState.trial.action"
          tag="p"
          scope="global"
          class="action-trial"
        >
          <template #device>
            <i18n-t
              keypath="visitors.timeline.emptyState.trial.deviceId"
              tag="p"
              scope="global"
              class="location-device-id"
            >
              <template #deviceId>
                <strong class="device-id">{{ locationDeviceId }}</strong>
              </template>
            </i18n-t>
          </template>
        </i18n-t>

        <div class="qr-code-title">
          {{ t("visitors.timeline.emptyState.trial.orQrCode") }}
        </div>

        <div class="qr-code">
          <img
            :src="qrCodeImage"
            alt=""
            class="qr-code-img"
          />
        </div>
      </EmptyState>

      <EmptyState
        v-else
        :icon="['fal', 'user-plus']"
        :heading="t('visitors.timeline.emptyState.heading')"
      >
        <i18n-t keypath="visitors.timeline.emptyState.action">
          <template #ipadTabletLink>
            <CoreLink
              type="primary"
              :underline="false"
              :route="{ name: 'devices' }"
            >
              {{ t("visitors.timeline.emptyState.ipadTablet") }}
            </CoreLink>
          </template>
          <template #qrCodeLink>
            <CoreLink
              type="primary"
              :underline="false"
              :route="{ name: 'locations' }"
            >
              {{ t("visitors.timeline.emptyState.qrCode") }}
            </CoreLink>
          </template>
        </i18n-t>
        <div class="action">
          <CoreButton
            type="primary"
            @click="emit('add-visit')"
          >
            {{ t("visitors.timeline.emptyState.addNewVisit") }}
          </CoreButton>
        </div>
      </EmptyState>
    </template>
  </DataTable>
</template>

<style lang="scss" scoped>
.method {
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
  gap: 10px;
}

.status {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.status-selector {
  margin-top: -10px;
}

.add-visit-btn {
  margin-left: 10px;
  width: 130px;

  @media screen and (max-width: $breakpoint-sm) {
    width: 110px;
  }
}

.action-dropdown :deep(.actions-button) {
  width: 130px;
  @media screen and (max-width: $breakpoint-sm) {
    min-width: unset;
    width: 110px;
  }
}

.action {
  margin-top: 1rem;
}

.filters-container {
  display: flex;
  gap: 10px;
  width: 100%;

  @media screen and (min-width: 1300px) {
    display: contents;
  }
  @media screen and (max-width: 414px) {
    flex-direction: column;
  }

  @media screen and ((min-width: 415px) and (max-width: 1309px)) {
    flex-direction: row;
  }
}

.subheading-trial {
  color: var(--so-text-color-regular);
  margin: 10px 0;
}

.description-trial,
.action-trial {
  color: var(--so-text-color-regular);
}

.location-device-id {
  color: var(--so-fill-color-blank);
  background-color: var(--so-counter-pill-color-type-green);
  border-radius: 4px;
  padding: 3px 5px;
  display: inline-block;
}

.device-id {
  font-weight: var(--font-bold);
  letter-spacing: 1px;
}

.qr-code {
  display: flex;
  justify-content: center;

  &-title {
    margin-top: 20px;
    color: var(--so-text-color-regular);
  }

  &-img {
    width: 121px;
    height: 121px;
    border-radius: 4px;
  }
}
</style>
